import fetcher from 'api/fetcher';
import { getValidateCampaignFields, getValidateErrors } from 'utils/campaign/validation';
import { checkAdditionalCropsNeeded, getActiveCropTypes } from 'utils/clickme';
import { isFirstStep } from 'utils/constants/campaign';
import { MagritteNotificationName } from 'utils/constants/notification';

import campaignEditStep1NextFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/common/campaign_edit_step1_next_form_submit';
import campaignEditStep2StartFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/common/campaign_edit_step2_start_form_submit';

import { saveBanner } from 'components/BannerBuilder/BannerBuilderActions/saveBanner';
import { saveCrops } from 'components/common/BannerImg/CropImageModal/saveCrops';
import { saveCampaign } from 'components/CpcFlexibleCreate/CpcActions/saveCampaign';
import { loadSourceFormConfig } from 'components/CpcFlexibleCreate/CpcForm/AdSources/loadSourceFormConfig';

import { BannerTextGenerationState } from 'store/models/bannerTextGeneration';
import { setIsCropImageModalVisible } from 'store/models/createBanner';
import { CreateCampaignState, setCampaignErrors, setCampaignFormStep } from 'store/models/createCampaign';
import { addMagritteNotification } from 'store/models/notification';
import { AppThunkAction } from 'store/utils';

type SetNextStepForCampaign = (
    currentStep: number,
    setSaving: (isSaving: boolean) => void,
    formCountSteps: number,
    dashboardRoute: string
) => AppThunkAction;

type SendInternalAnalytics = (
    state: CreateCampaignState & BannerTextGenerationState,
    currentStep: number,
    errorsObject: Record<string, string>
) => void;

const sendInternalAnalytics: SendInternalAnalytics = (state, currentStep, errorsObject) => {
    const {
        campaignId,
        adAgeTo: ageMax,
        adAgeFrom: ageMin,
        adLanguage,
        adProfessions,
        areas,
        adGender: sex,
        adSource,
        adUnknownAge: unknownAge,
        adTotalBudget: budget,
        adDailyBudget: budgetDaily,
        bannerTextGenerationCount,
        bannerTextType,
    } = state;

    const campaignIdNumber = Number(campaignId);
    const languages = JSON.stringify(adLanguage);
    const professions = JSON.stringify(adProfessions);
    const regions = JSON.stringify(areas);
    const sources = JSON.stringify(adSource);
    const errors = Object.keys(errorsObject).length !== 0 ? JSON.stringify(errorsObject) : null;

    if (isFirstStep(currentStep)) {
        campaignEditStep1NextFormSubmit({
            campaignId: campaignIdNumber,
            ageMax,
            ageMin,
            languages,
            professions,
            regions,
            sex,
            sources,
            unknownAge,
            errors,
        });
        return;
    }
    campaignEditStep2StartFormSubmit({
        campaignId: campaignIdNumber,
        budget,
        budgetDaily,
        budgetSources: '',
        manualBudgets: false,
        errors,
        bannerTextType,
        triesCount: bannerTextGenerationCount,
    });
};

export const setNextStepForCampaign: SetNextStepForCampaign = (
    currentStep,
    setSaving,
    formCountSteps,
    dashboardRoute
) => {
    return async (dispatch, getState) => {
        const { createBanner, createCampaign, profRoles, bannerTextGeneration } = getState();
        const { adSource } = createCampaign;
        const stateForValidate = { ...createBanner, ...createCampaign, profRoles };

        const { isCropsSaved, images } = createBanner;
        const fields = getValidateCampaignFields(currentStep, stateForValidate);
        try {
            const activeCropTypes = getActiveCropTypes(adSource);
            setSaving(true);
            if (currentStep === formCountSteps && isCropsSaved) {
                await dispatch(saveCrops(activeCropTypes));
            }
            const errors = await fetcher.post<unknown, Record<string, string>>(`/api/v1/validator/campaign`, fields);

            sendInternalAnalytics({ ...createCampaign, ...bannerTextGeneration }, currentStep, errors);

            if (Object.keys(errors).length > 0) {
                const stateErrors = getValidateErrors(errors);
                dispatch([
                    setCampaignErrors(stateErrors),
                    addMagritteNotification(MagritteNotificationName.CampaignFormFieldsError),
                ]);
            } else if (currentStep < formCountSteps) {
                dispatch([setCampaignFormStep(currentStep + 1), setCampaignErrors({})]);
                await dispatch(loadSourceFormConfig());
            } else if (currentStep === formCountSteps) {
                const isNeedAdditionalCrops = checkAdditionalCropsNeeded(adSource, images);

                if (!isCropsSaved && isNeedAdditionalCrops) {
                    dispatch(setIsCropImageModalVisible(true));
                    return;
                }
                if (isCropsSaved) {
                    await dispatch(saveBanner(activeCropTypes, dashboardRoute, false));
                    await dispatch(saveCampaign(dashboardRoute));
                } else {
                    await dispatch(saveCampaign(dashboardRoute));
                }
            }
        } catch (e) {
            dispatch(addMagritteNotification(MagritteNotificationName.CreateCampaignSomethingWentWrongError));
        } finally {
            setSaving(false);
        }
    };
};
